import { sapAssetsService } from "@/apis/services/sap-assets/sapAsset.service"
import { IGetEmployeeByIdParams, IPaginationMeta, IResponse } from "@/types"
import { ISapAsset, ISapAssetDto, ISapAssetPreview, ISapAssetUpdateDto } from "@/types/sapAsset.type"
import { create } from "zustand"
import { devtools } from "zustand/middleware"

interface ISapAssetState {
  assets: ISapAsset[]
  currentAsset: ISapAsset | null
  preview: IResponse<ISapAssetPreview> | null
  meta?: IPaginationMeta
  isLoading: boolean
  error: string | null
  fetchSapAssets: (params: IGetEmployeeByIdParams) => Promise<void>
  fetchSapAssetById: (recordId: string) => Promise<void>
  createSapAsset: (dto: ISapAssetDto) => Promise<void>
  updateSapAsset: (recordId: string, dto: ISapAssetUpdateDto) => Promise<void>
  previewSapAsset: (file: File) => Promise<IResponse<ISapAssetPreview>>
}

const initialState = {
  assets: [],
  currentAsset: null,
  preview: null,
  meta: undefined,
  isLoading: false,
  error: null,
}

export const usaSapAssetStore = create<ISapAssetState>()(
  devtools(set => ({
    ...initialState,

    fetchSapAssets: async (params: IGetEmployeeByIdParams) => {
      set({ isLoading: true, error: null })
      try {
        const response = await sapAssetsService.getSapAssets(params)
        set({ assets: response.data.items, meta: response.data.meta })
      } catch (error) {
        const errorMessage: string = error instanceof Error ? error.message : "An unknown error occurred"
        set({ error: errorMessage })
        throw error
      } finally {
        set({ isLoading: false })
      }
    },

    fetchSapAssetById: async (recordId: string) => {
      set({ isLoading: true, error: null })
      try {
        const response = await sapAssetsService.getSapAssetByRecordId(recordId)
        set({ currentAsset: response.data })
      } catch (error) {
        const errorMessage: string = error instanceof Error ? error.message : "An unknown error occurred"
        set({ error: errorMessage })
        throw error
      } finally {
        set({ isLoading: false })
      }
    },

    createSapAsset: async (dto: ISapAssetDto) => {
      set({ isLoading: true, error: null })
      try {
        await sapAssetsService.createSapAsset(dto)
      } catch (error) {
        const errorMessage: string = error instanceof Error ? error.message : "An unknown error occurred"
        set({ error: errorMessage })
        throw error
      } finally {
        set({ isLoading: false })
      }
    },

    updateSapAsset: async (recordId: string, dto: ISapAssetUpdateDto) => {
      set({ isLoading: true, error: null })
      try {
        await sapAssetsService.updateSapAsset(recordId, dto)
      } catch (error) {
        const errorMessage: string = error instanceof Error ? error.message : "An unknown error occurred"
        set({ error: errorMessage })
        throw error
      } finally {
        set({ isLoading: false })
      }
    },

    previewSapAsset: async (file: File) => {
      set({ isLoading: true, error: null })
      try {
        const response = await sapAssetsService.previewAsset(file)
        set({ preview: response })
        return response
      } catch (error) {
        const errorMessage: string = error instanceof Error ? error.message : "An unknown error occurred"
        set({ error: errorMessage })
        throw error
      } finally {
        set({ isLoading: false })
      }
    },
  }))
)
